import { useEffect, useMemo } from 'react';
import {
	FIELDS_DETAIL_PREFERENCE,
	FIELDS_PREFERENCE,
} from '@atlassian/jira-business-preferences/src/constants.tsx';
import { useViewPreference } from '@atlassian/jira-business-preferences/src/controllers/view-preferences-context/index.tsx';
import type { FieldsDetailPreference } from '@atlassian/jira-business-preferences/src/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common/src/controllers/business-entity-context/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useJWMListFeatures } from '../../features-context/index.tsx';
import { getInitVisibleFields } from '../constants/index.tsx';

// Can remove `fromUpdateFieldDetails` once jira_unread_comments_in_list_view is cleaned up
// We are using `fromUpdateFieldDetails` to control when we fire track analytics when fields are initialised,
// to avoid firing track analytics execesively
export const useFieldsPreference = (fromUpdateFieldDetails?: boolean) => {
	const [fields, setFieldsOld] = useViewPreference(FIELDS_PREFERENCE);
	const [fieldsDetail, setFields] = useViewPreference(FIELDS_DETAIL_PREFERENCE);
	const { isBusinessFieldsEnabled, isScrumProject } = useJWMListFeatures();
	const { data: entity } = useBusinessEntity();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// We want to only fire track analytics once when fields are being initialised
	useEffect(() => {
		if (
			fromUpdateFieldDetails &&
			(fieldsDetail == null || !Array.isArray(fieldsDetail.order)) &&
			expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
		) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'commentsColumn enabled', {
				location: 'Initialising fields',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fieldsPreferences = useMemo<FieldsDetailPreference>(() => {
		if (fieldsDetail == null && fields != null) {
			return {
				order: fields,
				details: null,
			};
		}

		if (fieldsDetail == null || !Array.isArray(fieldsDetail.order)) {
			return {
				order: getInitVisibleFields({
					isCategoryEnabled: isBusinessFieldsEnabled,
					isScrumProject,
					...(expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
						? {
								entityId: entity.id,
							}
						: {}),
				}),
				details: null,
			};
		}

		return fieldsDetail;
	}, [fieldsDetail, fields, isBusinessFieldsEnabled, isScrumProject, entity.id]);

	return [fieldsPreferences, setFields, setFieldsOld] as const;
};
